import React, { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useCollect } from 'hooks';

import Show from 'components/Show';
import Editor from 'components/Editor';
import Avatar from 'components/Misc/Avatar';
import Progress from 'components/Misc/Progress';
import { Dates, Title } from 'components/Misc/Typography';

import Materials from 'containers/Student/Courses/Profile/Materials';
import Webinars from 'containers/Student/Courses/Profile/Webinars';
import Exams from 'containers/Student/Courses/Profile/Exams';

import { useAppStore } from 'store/useAppStore';

const Profile = () => {
	// Cache current object's id
	const { id } = useParams();

	// Use app's store
	const setTitle = useAppStore(state => state.setTitle);

	// Store request parameters
	const parameters = useMemo(() => ({
		eager: '[subject, teacher]'
	}), []);

	// Store requested course
	const [[course, progress], collect] = useCollect(`courses/${id}`, parameters);

	// Set page's title
	useEffect(() => setTitle(course?.titlePublic), [course]);

	// Collect course on mount and on parameter change
	useEffect(() => {
		(async () => await collect())();
	}, [collect]);

	return (
		<div className="space-y-4 mx-auto max-w-screen-md">
			<div className="pt-3 px-4 pb-4 space-y-2 rounded-lg bg-white dark:bg-neutral-800 transition-colors">
				{/* Show progress */}
				<Progress progress={progress} />

				{/* Show course collected */}
				<Show if={!progress && course?.id}>
					{/* Course's information */}
					<div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0 sm:items-center">
						<Title
							title={course?.title}
							subtitle={course?.subject?.title}
							className="flex-1 text-xl"
						/>

						<Dates
							startDate={course?.startDate}
							endDate={course?.endDate}
						/>
					</div>

					{/* Course's description */}
					<Show if={course?.description}>
						<Editor
							readOnly
							path="courses"
							folder={course?.slug}
							value={course?.description}
						/>
					</Show>

					{/* Course's teacher */}
					<Show if={course?.teacher?.id}>
						<div className="flex items-center space-x-3">
							<Title
								title={course?.teacher?.fullName}
								className="flex-1 text-right whitespace-nowrap text-ellipsis overflow-hidden"
							/>

							<Avatar
								alt={course?.teacher?.fullName}
								src={course?.teacher?.avatar}
							/>
						</div>
					</Show>
				</Show>
			</div>

			{/* Student course's exams */}
			<Show if={!course?.additional}>
				<Exams />
			</Show>

			{/* Student course's webinars */}
			<Webinars />

			{/* Student course's materials */}
			<Materials />
		</div>
	);
};

export default Profile;